import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  FormProps,
  Row,
  Col,
  Input,
  // AutoComplete,
  Select,
  DatePicker,
  InputNumber,
  DatePickerProps,
  Button,
  Table,
  notification,
  Checkbox
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { PlusOutlined, CalendarOutlined, CloseOutlined } from '@ant-design/icons';

import GenerateGroupBookingModal from 'components/modal/group/generate-group-booking-modal';

import { useGetTravelAgencies } from 'hooks/useGetTravelAgencies';
import { useGetSources } from 'hooks/useGetSources';
import { useGetCompanies } from 'hooks/useGetCompanies';
import { useGetGroupBookingDetail } from 'hooks/useGetGroupBookings';
import useModalStore from 'stores/useModal';

import { getMessageCode } from 'constants/validate';
import { getVipLevelOptions } from 'constants/form';
import { ACTION_SUBGROUP, getColumnDetailGroup } from 'constants/table';
import { ParamsAddSubGroup, ParamsGroupBooking, SubGroup } from 'services/api/type/group.type';
// import { RangeValueType } from 'services/api/type/common.type';
// import { getDateTodayMin2PM, getNextDateAt } from 'utils';

import {
  cancelSubgroup,
  updateGroupBooking,
  cancelGroupBooking
} from 'services/api/module/group.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
// import { ReactComponent as IconMale } from 'assets/images/male.svg';
// import { ReactComponent as IconEmail } from 'assets/images/email.svg';
import 'styles/detail-group.scss';
import useCashierStore from 'stores/useCashier';
import PAGES from 'routes/constants';
import { formatInputNumber } from 'utils/currency';
import useCommonStore from 'stores/useCommon';
import { useTranslation } from 'react-i18next';

export const disabledLastDeparture = (current: Dayjs, arrival: Dayjs) => {
  // Can not select days after today and today
  return current && current <= arrival;
};

const today = dayjs();
const disabledDate: DatePickerProps['disabledDate'] = current => {
  return current.isBefore(today);
};

type TotalRowType = SubGroup & {
  isTotalRow: boolean;
};

function DetailGroup() {
  const { t } = useTranslation();
  const VIP_LEVEL_OPTIONS = getVipLevelOptions(t);
  const MESSAGE_CODE = getMessageCode(t);
  const { groupId } = useParams();
  const [form] = Form.useForm();
  const nameForm = Form.useWatch('name', form);
  const arrivalForm = Form.useWatch('first_arrival', form);

  const { setSelectedGroupId } = useCashierStore();
  const { currentBranch } = useCommonStore();
  const navigate = useNavigate();

  const {
    subGroupDetail,
    setIsOpenSubGroupDetail,
    setSubGroupDetail,
    setIsOpenChangeDateGroupBooking,
    setInfoConfirmModal,
    setConfirmLoading,
    openPostDummyRoomCharge,
    setIsOpenViewGroupInvoice
  } = useModalStore();

  // const [searchName, setSearchName] = useState('');
  const [searchCompany, setSearchCompany] = useState({});
  // const [rangeDate, setRangeDate] = useState<RangeValueType>([
  //   getDateTodayMin2PM(),
  //   getNextDateAt(1, 12)
  // ]);
  const [subGroups, setSubGroups] = useState<SubGroup[]>([]);
  const [isShowGenerateGroup, setIsShowGenerateGroup] = useState(false);

  const { data: companies } = useGetCompanies();
  const { data: sources } = useGetSources();

  // const { data: customers } = useGetCustomers(
  //   searchName,
  //   CUSTOMER_TYPE.PERSONAL,
  //   !!searchName && searchName.length > 3
  // );
  const { data: dataCompany, isLoading } = useGetTravelAgencies(searchCompany);

  const { data: dataGroup, isFetching: isFetchingGroup } = useGetGroupBookingDetail(
    Number(groupId)
  );
  const totals = useMemo(
    () => ({
      rooms: subGroups.reduce((acc, curr) => acc + curr.quantity, 0),
      roomBooked: subGroups.reduce((acc, curr) => acc + curr.roomed, 0),
      guest: subGroups.reduce((acc, curr) => acc + curr.pax, 0),
      roomedPax: subGroups.reduce((acc, curr) => acc + curr.roomed_pax, 0),
      rateAmount: subGroups.reduce((acc, curr) => {
        const startDate = dayjs(curr.check_in);
        const endDate = dayjs(curr.check_out);
        const night = endDate
          .set('hour', 23)
          .set('minute', 59)
          .diff(startDate.set('hour', 0).set('minute', 0), 'day');
        return acc + curr.rate_amount * night * curr.quantity;
      }, 0)
    }),
    [subGroups]
  );

  const isCancelled: boolean = dataGroup?.state === 'cancelled';

  const { mutateAsync: mutateCancelSubGroup } = useMutation({
    mutationFn: (params: ParamsAddSubGroup) => {
      return cancelSubgroup(Number(subGroupDetail?.id), params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GROUP_BOOKING_DETAIL, Number(groupId)]
      });
    }
  });

  const { mutateAsync: mutateUpdateSubGroup, isPending: isPendingUpdate } = useMutation({
    mutationFn: (params: Partial<ParamsGroupBooking>) => {
      return updateGroupBooking(Number(groupId), params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GROUP_BOOKING_DETAIL, Number(groupId)]
      });
    }
  });

  const { mutateAsync: mutateCancelGroupBooking, isPending: isPendingCancelGroupBooking } =
    useMutation({
      mutationFn: () => {
        return cancelGroupBooking(Number(groupId));
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_GROUP_BOOKING_DETAIL, Number(groupId)]
        });
      }
    });

  const totalBalance = useMemo(() => {
    return dataGroup?.total_balance.reduce((cur, next) => {
      return cur + next;
    }, 0);
  }, [dataGroup]);

  useEffect(() => {
    const pmsAppContent = document.querySelector('.pms-app-content');
    if (pmsAppContent) {
      pmsAppContent.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    if (!_.isEmpty(dataGroup)) {
      const startDate = dayjs(dataGroup.first_arrival);
      const endDate = dayjs(dataGroup.last_departure);
      const night = endDate
        .set('hour', 23)
        .set('minute', 59)
        .diff(startDate.set('hour', 0).set('minute', 0), 'day');

      form.setFieldsValue({
        id: dataGroup.id,
        name: dataGroup.name,
        booker: null,
        hotel_travel_agency_id: dataGroup.hotel_travel_agency_id,
        // status: '',
        vip_level: dataGroup.vip_level,
        medium_id: dataGroup.medium_id,
        source_id: dataGroup.source_id,
        first_arrival: startDate,
        last_departure: endDate,
        release_date: dayjs(dataGroup.release_date),
        night,
        note: dataGroup.note ? dataGroup.note : '',
        pricelist_id: '',
        ref_code: dataGroup.ref_code,
        has_breakfast: dataGroup.has_breakfast,
        total_balance: totalBalance
      });
      setSubGroups(dataGroup.sub_groups);
    }
  }, [dataGroup, form, totalBalance]);

  // const { data: availRoomTypes, isFetching: isFetchingRoomTypes } = useGetAvailRoomType(
  //   toUtcTime(rangeDate[0], 'YYYY-MM-DD HH:mm:ss'),
  //   toUtcTime(rangeDate[1], 'YYYY-MM-DD HH:mm:ss')
  // );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // const handleSearchName = useCallback(
  //   _.debounce((value: string) => {
  //     setSearchName(value);
  //   }, 500),
  //   []
  // );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchCompany = useCallback(
    _.debounce((value: string) => {
      setSearchCompany({ name: value });
    }, 500),
    []
  );

  // const handleSelectCustomerAutoComplete = (customerId: number) => {
  //   const customer = _.find(customers, { id: customerId });

  //   if (customer) {
  //     const data: {
  //       [key: string]: any;
  //     } = {
  //       partner_id: customer.id,
  //       name: customer.name,
  //       gender: customer.gender,
  //       phone: customer.phone,
  //       email: customer.email,
  //       dob: customer.dob ? dayjs(customer.dob) : undefined,
  //       identification: customer.identification,
  //       country_id: customer.country?.id,
  //       state_id: customer.state?.id,
  //       street: customer.street,
  //       comment: customer.comment,
  //       attachments: customer.attachments
  //     };
  //     const truthyValues: any = Object.keys(data)
  //       .filter(key => Boolean(data[key]))
  //       .reduce((cur, next) => {
  //         return {
  //           ...cur,
  //           [next]: data[next]
  //         };
  //       }, {});

  //     form.setFieldValue('booker', truthyValues);
  //   }
  // };

  const handleChangeArrival = (day: Dayjs) => {
    const lastDeparture = day.add(1, 'day');
    form.setFieldValue('lastDeparture', lastDeparture);

    const newBookings = subGroups.map(item => {
      return {
        ...item,
        from_date: day,
        to_date: lastDeparture
      };
    });
    setSubGroups(newBookings);
    form.setFieldValue('night', 1);

    // let startDate = day;
    // const now = dayjs();
    // const minNow = now.hour(14).minute(0).second(0).millisecond(0);
    // startDate = now.valueOf() > minNow.valueOf() ? now : minNow;
    // const endDate = startDate.add(1, 'day').hour(12).minute(0).second(0).millisecond(0);
    // setRangeDate([startDate, endDate]);
  };

  const handleChangeDeparture = (day: Dayjs) => {
    const startDate: Dayjs = _.clone(arrivalForm);

    const endDate: Dayjs = _.clone(day);
    const night = endDate
      .set('hour', 23)
      .set('minute', 59)
      .diff(startDate.set('hour', 0).set('minute', 0), 'day');
    form.setFieldValue('night', night);

    // setRangeDate([startDate.hour(14).minute(0).second(0), endDate.hour(12).minute(0).second(0)]);
  };

  // const handleChangePricing = (value: number) => {
  //   const newBookings = bookings.map(item => {
  //     const roomType = _.find(availRoomTypes, { room_type_name: item.room_type_name });
  //     const rateAmount = _.find(roomType?.pricing, { pricelist_id: value });
  //     return {
  //       ...item,
  //       rateAmount: rateAmount?.price || 0
  //     };
  //   });
  //   setSubGroups(newBookings);
  // };

  const handleShowAddNewSubGroup = () => {
    setSubGroupDetail(undefined);
    setIsOpenSubGroupDetail(true);
  };

  const handleShowChangeDate = () => {
    setIsOpenChangeDateGroupBooking(true);
  };

  const handleCancelSubGroup = async (subGroupInfo: SubGroup | undefined) => {
    if (subGroupInfo) {
      try {
        setConfirmLoading(true);
        await mutateCancelSubGroup({
          check_in: dayjs(subGroupInfo?.check_in).format('YYYY-MM-DD'),
          check_out: dayjs(subGroupInfo?.check_out).format('YYYY-MM-DD'),
          room_type_id: subGroupInfo.room_type_id,
          quantity: subGroupInfo.quantity,
          rate_amount: subGroupInfo.rate_amount,
          pax: subGroupInfo.pax
        });

        setInfoConfirmModal(false);
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.error')
        });
      } finally {
        setConfirmLoading(false);
      }
    }
  };

  const confirmCancelGroupBooking = () => {
    setInfoConfirmModal(true, {
      title: t('groupPage.groupBooking.confirmCancelBooking'),
      onOk: () => {
        handleCancelGroupBooking();
      }
    });
  };

  const handleCancelGroupBooking = async () => {
    try {
      await mutateCancelGroupBooking();
      notification.success({
        message: t('groupPage.groupBooking.cancelBookingSuccessfully')
      });
    } catch (err: any) {
      notification.error({
        message: err.error || t('groupPage.groupBooking.cancelBookingFailed')
      });
    } finally {
      setInfoConfirmModal(false);
    }
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    try {
      const params: Partial<ParamsGroupBooking> = {
        name: values.name,
        hotel_travel_agency_id: values.hotel_travel_agency_id,
        release_date: values.release_date.format('YYYY-MM-DD'),
        has_breakfast: Boolean(values.has_breakfast)
      };

      ['vip_level', 'medium_id', 'source_id', 'note', 'ref_code'].forEach(key => {
        if (values[key]) {
          _.set(params, key, values[key]);
        }
      });

      // await fetch(
      //   `https://api-pms-dev.kinliving.vn/api${paths.updateGroupBooking(Number(groupId))}`,
      //   {
      //     method: 'PUT',
      //     body: JSON.stringify(params),
      //     headers: {
      //       'Content-Type': 'application/json',
      //       'Access-Control-Allow-Origin': '*',
      //       'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept, Z-Key',
      //       'Access-Control-Allow-Methods': 'GET, HEAD, POST, PUT, DELETE, OPTIONS',
      //       Authorization:
      //         'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjo4OSwiaXNfZXh0ZXJuYWwiOmZhbHNlLCJleHAiOjE3MjYwNTQ5NTR9.BXnoIvlRVQEXOMUVwVrQMwSlRpAOrdl-7JZIxVS6-N8|1'
      //     }
      //   }
      // );

      await mutateUpdateSubGroup(params);

      notification.success({
        message: t('groupPage.groupBooking.editGroupSuccessfully')
      });
    } catch (err: any) {
      notification.error({
        message: err.error || t('common.error')
      });
    }
  };

  const detailGroupCols = useMemo(() => {
    const handleClickMenuSubgroup = (menu: any) => {
      switch (menu.key) {
        case ACTION_SUBGROUP.DETAIL:
          setIsOpenSubGroupDetail(true);
          break;
        case ACTION_SUBGROUP.GENERATE_ROOM_LIST:
          setIsShowGenerateGroup(true);
          break;
        case ACTION_SUBGROUP.CANCEL_GROUP:
          setInfoConfirmModal(true, {
            title: t('groupPage.subGroup.confirmDelete'),
            onOk: () => {
              handleCancelSubGroup(subGroupDetail);
            }
          });
          break;
        default:
          break;
      }
    };

    const columns = getColumnDetailGroup(t, handleClickMenuSubgroup);

    const modifiedColumns = columns.map((col, index) => {
      if (index === 0) {
        return {
          ...col,
          render: (value: number, record: TotalRowType, rowIndex: number) => {
            if (record.isTotalRow) {
              return {
                children: <strong>{t('common.total')}</strong>,
                props: { colSpan: 2 }
              };
            }
            return col.render ? col.render(value, record, rowIndex) : rowIndex + 1;
          }
        };
      }

      if (index === 1) {
        return {
          ...col,
          render: (value: number, record: TotalRowType, rowIndex: number) =>
            record.isTotalRow
              ? { children: null, props: { colSpan: 0 } }
              : col.render
                ? col.render(value, record, rowIndex)
                : value
        };
      }

      return col;
    });

    return modifiedColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, subGroupDetail]);

  // const renderCustomerOptions = useMemo(() => {
  //   return customers?.map(item => ({
  //     label: (
  //       <div className="customer-autocomplete-item">
  //         <div className="flex items-center justify-between">
  //           <div className="flex items-center" style={{ gap: '8px' }}>
  //             <IconMale />
  //             <p className="m-0 truncate" style={{ maxWidth: '200px' }}>
  //               {item.name}
  //             </p>
  //           </div>
  //           <div className="flex items-center" style={{ gap: '8px' }}>
  //             <IdcardOutlined />
  //             <p className="m-0 truncate" style={{ maxWidth: '200px' }}>
  //               {item.identification || '-'}
  //             </p>
  //           </div>
  //         </div>
  //         <div className="flex items-center justify-between">
  //           <div className="flex items-center" style={{ gap: '8px' }}>
  //             <IconEmail />
  //             <p className="m-0 truncate" style={{ maxWidth: '200px' }}>
  //               {item.email || '-'}
  //             </p>
  //           </div>
  //           <div className="flex items-center" style={{ gap: '8px' }}>
  //             <PhoneFilled />
  //             <p className="m-0 truncate" style={{ maxWidth: '200px' }}>
  //               {item.phone || '-'}
  //             </p>
  //           </div>
  //         </div>
  //       </div>
  //     ),
  //     value: item.id
  //   }));
  // }, [customers]);

  const handleCreateOrUpdateDeposit = () => {
    openPostDummyRoomCharge(dataGroup);
  };

  const handleRedirectToCashier = () => {
    setSelectedGroupId(String(groupId));
    navigate(PAGES.cashier);
  };

  const handlePrintGroupReceipt = () => {
    setIsOpenViewGroupInvoice(true, Number(groupId));
  };

  return (
    <div className="pms-detail-group">
      <Card title={t('groupPage.groupDetail.title')} style={{ width: '100%' }}>
        <Form
          form={form}
          name="detail-group-form"
          layout="horizontal"
          initialValues={{
            name: '',
            booker: null,
            hotel_travel_agency_id: '',
            code: '',
            vip_level: '',
            medium_id: '',
            source_id: '',
            first_arrival: dayjs(),
            last_departure: dayjs().add(1, 'day'),
            release_date: dayjs(),
            night: 1,
            note: '',
            pricelist_id: ''
          }}
          onFinish={onFinish}
          autoComplete="off"
          wrapperCol={{ span: 18 }}
          labelCol={{ span: 6 }}
        >
          <div className="form-inner">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} xl={7}>
                <Form.Item
                  name="name"
                  label={t('groupPage.createGroup.groupName')}
                  rules={[
                    {
                      required: true,
                      message: MESSAGE_CODE.REQUIRED_GROUP_NAME
                    }
                  ]}
                  labelCol={{ span: 9 }}
                  wrapperCol={{ span: 15 }}
                  labelAlign="left"
                >
                  <Input className="w-full" />
                </Form.Item>

                {/* <Form.Item
                  label="Người đặt"
                  name={['booker', 'name']}
                  style={{ flexGrow: 5 }}
                  rules={[
                    {
                      required: true,
                      message: MESSAGE_CODE.REQUIRED_FULL_NAME
                    }
                  ]}
                >
                  <AutoComplete
                    allowClear
                    placeholder="Tìm kiếm tên, phone hoặc tạo mới"
                    popupClassName="autocomplete-customer"
                    onSearch={handleSearchName}
                    onSelect={handleSelectCustomerAutoComplete}
                    options={renderCustomerOptions}
                    popupMatchSelectWidth={false}
                  />
                </Form.Item> */}

                <Form.Item
                  label={t('groupPage.groupDetail.taCompany')}
                  name="hotel_travel_agency_id"
                  labelCol={{ span: 9 }}
                  wrapperCol={{ span: 15 }}
                  labelAlign="left"
                >
                  <Select
                    showSearch
                    allowClear
                    onClear={() => setSearchCompany({})}
                    placeholder={t('groupPage.createGroup.selectCompany')}
                    optionFilterProp="children"
                    loading={isLoading}
                    onSearch={handleSearchCompany}
                    onSelect={value => {
                      const selectedCompany = dataCompany?.find(item => item.id === value);
                      if (dataGroup?.hotel_travel_agency_name !== selectedCompany?.name) {
                        form.setFieldsValue({
                          source_id: selectedCompany?.source_id,
                          medium_id: selectedCompany?.medium_id
                        });
                      } else {
                        form.setFieldsValue({
                          source_id: dataGroup?.source_id,
                          medium_id: dataGroup?.medium_id
                        });
                      }
                    }}
                  >
                    {dataCompany?.map(item => (
                      <Select.Option key={item.id} value={item.id}>
                        {`${item.id} - ${item.name}`}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label={t('managePage.breakfastPage.breakfast')}
                  name="has_breakfast"
                  valuePropName="checked"
                  labelCol={{ span: 9 }}
                  wrapperCol={{ span: 15 }}
                  labelAlign="left"
                >
                  <Checkbox />
                </Form.Item>
                {/* <Form.Item label="Loại giá" name="pricing">
                  <Select options={pricelistOptions} onChange={handleChangePricing} />
                </Form.Item> */}
              </Col>

              <Col xs={24} md={12} xl={9}>
                <Form.Item
                  name="id"
                  label={t('groupPage.groupList.groupCode')}
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                >
                  <Input className="w-full" disabled />
                </Form.Item>
                <Form.Item
                  name="ref_code"
                  label={t('groupPage.groupList.refCode')}
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                >
                  <Input className="w-full" />
                </Form.Item>

                {/* <Form.Item label="Status" name="status">
                  <Select />
                </Form.Item> */}

                <Form.Item
                  label={t('groupPage.groupList.vipLevel')}
                  name="vip_level"
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                >
                  <Select options={VIP_LEVEL_OPTIONS} />
                </Form.Item>

                <Form.Item
                  name="medium_id"
                  label={t('groupPage.createGroup.medium')}
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                >
                  <Select
                    options={companies}
                    showSearch
                    filterOption={(input, option) =>
                      !!option && option.name.toLowerCase().includes(input.toLowerCase())
                    }
                    fieldNames={{ label: 'name', value: 'id' }}
                  />
                </Form.Item>

                <Form.Item
                  name="source_id"
                  label={t('groupPage.createGroup.source')}
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                >
                  <Select
                    options={sources}
                    showSearch
                    filterOption={(input, option) =>
                      !!option && option.name.toLowerCase().includes(input.toLowerCase())
                    }
                    fieldNames={{ label: 'name', value: 'id' }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={18} xl={8}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Form.Item
                      name="first_arrival"
                      label={t('groupPage.createGroup.firstArrival')}
                      wrapperCol={{ span: 12 }}
                      labelCol={{ span: 12 }}
                    >
                      <DatePicker
                        onChange={handleChangeArrival}
                        disabledDate={disabledDate}
                        disabled
                      />
                    </Form.Item>

                    <Form.Item
                      name="last_departure"
                      label={t('groupPage.createGroup.lastDeparture')}
                      wrapperCol={{ span: 12 }}
                      labelCol={{ span: 12 }}
                    >
                      <DatePicker
                        onChange={handleChangeDeparture}
                        disabledDate={current => disabledLastDeparture(current, arrivalForm)}
                        disabled
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="night"
                      label={t('groupPage.createGroup.night')}
                      wrapperCol={{ span: 12 }}
                      labelCol={{ span: 12 }}
                    >
                      <InputNumber className="w-full" readOnly />
                    </Form.Item>

                    <Form.Item
                      name="release_date"
                      label={t('groupPage.createGroup.releaseDate')}
                      wrapperCol={{ span: 12 }}
                      labelCol={{ span: 12 }}
                    >
                      <DatePicker disabledDate={disabledDate} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="note" label={t('common.note')} style={{ marginLeft: '-4px' }}>
                  <Input.TextArea rows={5} className="w-full" />
                </Form.Item>
                <Form.Item name="total_balance" label={t('cashierPage.totalBalance')}>
                  <InputNumber
                    style={{ width: '100%' }}
                    readOnly
                    formatter={value => formatInputNumber(Number(value))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="detail-group-actions">
            {currentBranch?.is_enable_v2 && (
              <>
                <Button className="ant-btn-secondary" onClick={handlePrintGroupReceipt}>
                  {t('cashierPage.invoice.confirm')}
                </Button>
                <Button
                  className="ant-btn-secondary"
                  onClick={handleRedirectToCashier}
                  disabled={!dataGroup?.has_booking}
                >
                  {t('header.cashier')}
                </Button>
                <Button
                  className="ant-btn-secondary"
                  onClick={handleCreateOrUpdateDeposit}
                  disabled={isCancelled}
                >
                  {t('groupPage.groupDetail.deposit')}
                </Button>
              </>
            )}
            <Button
              className="ant-btn-secondary"
              htmlType="submit"
              loading={isPendingUpdate}
              disabled={isCancelled}
            >
              {t('groupPage.groupDetail.updateGroup')}
            </Button>
            <Button
              className="ant-btn-secondary"
              icon={<CloseOutlined />}
              loading={isPendingCancelGroupBooking}
              onClick={confirmCancelGroupBooking}
              disabled={isCancelled}
            >
              {t('groupPage.groupDetail.cancelGroup')}
            </Button>
          </div>
        </Form>
      </Card>

      <div className="detail-group-table">
        <Card title={t('groupPage.subGroup.title')} className="w-full">
          <h1>
            {t('header.group')} {nameForm}
          </h1>
          <Table
            rowKey="id"
            loading={isFetchingGroup}
            columns={detailGroupCols}
            dataSource={[
              ...subGroups,
              {
                id: 'total-row',
                room_type_name: '',
                quantity: totals.rooms,
                roomed: totals.roomBooked,
                pax: totals.guest,
                roomed_pax: totals.roomedPax,
                check_in: '',
                check_out: '',
                rate_amount: '',
                rmTotal: totals.rateAmount,
                isTotalRow: true
              } as any
            ]}
            locale={{
              emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
            }}
            scroll={{ x: 1000, y: 'calc(100vh - 320px)' }}
            pagination={false}
            onRow={record => {
              return {
                onClick: () => {
                  if (!record.isTotalRow) {
                    setSubGroupDetail(record);
                  }
                }, // click row
                className: record.isTotalRow ? 'total-row' : ''
              };
            }}
          />

          <div className="detail-group-table__actions">
            <Button
              className="ant-btn-secondary"
              icon={<PlusOutlined />}
              onClick={handleShowAddNewSubGroup}
              disabled={isCancelled}
            >
              {t('groupPage.subGroup.addSubGroup')}
            </Button>

            <Button
              className="ant-btn-secondary"
              icon={<CalendarOutlined />}
              onClick={handleShowChangeDate}
              disabled={isCancelled}
            >
              {t('groupPage.subGroup.editDate')}
            </Button>
          </div>
        </Card>
      </div>

      <GenerateGroupBookingModal
        isShow={isShowGenerateGroup}
        onCancel={() => setIsShowGenerateGroup(false)}
      />
    </div>
  );
}

export default DetailGroup;
